import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { VotingList } from "../../domain/vote/Models/VotingList"
import { Voting } from "../../domain/vote/Models/Voting"
import { VotingInput } from "../../domain/vote/Models/VotingInput"
import type { PendingVotingDocSerialized } from "../../domain/vote/Models/PendingVotingDoc"
import type { RefreshAutoUpdatableAllowlistsResult } from "../../domain/vote/Models/RefreshAutoUpdatableAllowlistsResult"
import { SafeProposal } from "../../domain/_kernel/SafeProposal"
import { Pagination } from "../../domain/_kernel/Pagination"
import {
  VOTING_STATUS_ENUM,
  VOTING_SCOPE_ENUM,
  OrderDirectionEnum,
  type TerritorialTargetType,
} from "@antipartido-inc/contracts-ifaces"
import { TerritorialTarget } from "../../domain/vote/Models/TerritorialTarget"
import { useSafeTransactionsStore } from "./safeTransactionsStore"

export const PAGE_SIZE = 20

interface VotingListFilters {
  readonly statusFilter: VOTING_STATUS_ENUM[]
  readonly search: string
  readonly currentPage: number
  readonly orderBy: string
  readonly orderDirection: OrderDirectionEnum
}

interface VotingListState extends VotingListFilters {
  readonly votingList?: VotingList
  readonly featuredVotings: Voting[]
  readonly totalCount: number
  readonly isLoading: boolean
  readonly error?: string
  readonly isSaving: boolean
  readonly isPublishing: boolean
  readonly isDeleting: boolean
  readonly isTogglingAutoUpdate: boolean
}

interface VotingListActions {
  readonly setStatusFilter: (statuses: VOTING_STATUS_ENUM[]) => void
  readonly setSearch: (search: string) => void
  readonly setCurrentPage: (page: number) => void
  readonly setSort: (orderBy: string, orderDirection: OrderDirectionEnum) => void
  readonly fetchVotingList: () => Promise<VotingList | undefined>
  readonly fetchFeaturedVoting: () => Promise<void>
  readonly createVoting: (input: {
    readonly votingInput: VotingInput
    readonly optionFiles?: Map<string, PendingVotingDocSerialized[]>
    readonly candidateImages?: Map<string, File>
    readonly votingFiles?: PendingVotingDocSerialized[]
  }) => Promise<Voting>
  readonly updateVoting: (input: {
    readonly votingInput: VotingInput
    readonly optionFiles?: Map<string, PendingVotingDocSerialized[]>
    readonly candidateImages?: Map<string, File>
    readonly votingFiles?: PendingVotingDocSerialized[]
  }) => Promise<Voting>
  readonly updatePublishedVoting: (input: {
    readonly id: string
    readonly scope?: VOTING_SCOPE_ENUM
    readonly territorialTarget?: TerritorialTargetType | null
  }) => Promise<Voting>
  readonly deleteVoting: (id: string) => Promise<void>
  readonly createTokenOnBlockchain: (votingId: string) => Promise<SafeProposal>
  readonly setVoteWindow: (input: { readonly votingId: string; readonly startDate: Date; readonly endDate: Date }) => Promise<SafeProposal>
  readonly toggleAutoUpdatableVoting: (input: { readonly votingId: string; readonly enabled: boolean }) => Promise<Voting>
  readonly refreshAutoUpdatableAllowlists: () => Promise<RefreshAutoUpdatableAllowlistsResult>
  readonly reset: () => void
}

type VotingListStore = VotingListState & VotingListActions

const initialFilters: VotingListFilters = {
  statusFilter: [],
  search: "",
  currentPage: 1,
  orderBy: "startDate",
  orderDirection: OrderDirectionEnum.DESC,
}

export const useVotingListStore = create<VotingListStore>()(
  devtools(
    (set, get) => ({
      ...initialFilters,
      votingList: undefined,
      featuredVotings: [],
      totalCount: 0,
      isLoading: false,
      error: undefined,
      isSaving: false,
      isPublishing: false,
      isDeleting: false,
      isTogglingAutoUpdate: false,

      setStatusFilter: (statuses) => {
        set({ statusFilter: statuses, currentPage: 1 })
      },

      setSearch: (search) => {
        set({ search, currentPage: 1 })
      },

      setCurrentPage: (page) => {
        set({ currentPage: page })
      },

      setSort: (orderBy, orderDirection) => {
        set({ orderBy, orderDirection, currentPage: 1 })
      },

      fetchFeaturedVoting: async () => {
        try {
          const { votingList } = await window.domain.GetVotingListVoteUseCase.execute({
            statuses: [VOTING_STATUS_ENUM.ACTIVE],
          })
          const featured = votingList.items.filter((v) => v.isAutoUpdatable)
          set({ featuredVotings: featured })
        } catch {
          // Featured voting is optional, silently ignore errors
        }
      },

      fetchVotingList: async () => {
        const state = get()
        if (state.isLoading) return undefined

        set({ isLoading: true, error: undefined })

        try {
          const pagination = Pagination.create({
            offset: (state.currentPage - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
            orderBy: state.orderBy,
            orderDirection: state.orderDirection,
          })

          const { votingList } = await window.domain.GetVotingListVoteUseCase.execute({
            statuses: state.statusFilter.length > 0 ? state.statusFilter : undefined,
            search: state.search || undefined,
            pagination,
          })

          const totalCount = votingList.pagination.numElements ?? votingList.items.length
          set({ votingList, totalCount, isLoading: false })
          return votingList
        } catch (error) {
          set({ error: error instanceof Error ? error.message : String(error), isLoading: false })
          throw error
        }
      },

      createVoting: async (input) => {
        set({ isSaving: true })
        try {
          const { voting } = await window.domain.CreateVotingVoteUseCase.execute(input)
          set({ votingList: undefined, isSaving: false })
          return voting
        } catch (error) {
          set({ isSaving: false })
          throw error
        }
      },

      updateVoting: async (input) => {
        set({ isSaving: true })
        try {
          const { voting } = await window.domain.UpdateVotingVoteUseCase.execute(input)
          set({ votingList: get().votingList?.replaceItem(voting), isSaving: false })
          return voting
        } catch (error) {
          set({ isSaving: false })
          throw error
        }
      },

      updatePublishedVoting: async (input) => {
        set({ isSaving: true })
        try {
          const rawTarget = input.territorialTarget ?? undefined
          const { voting } = await window.domain.UpdatePublishedVotingVoteUseCase.execute({
            id: input.id,
            scope: input.scope,
            territorialTarget: rawTarget ? TerritorialTarget.createFromAPI(rawTarget) : undefined,
            clearTerritorialTarget: input.territorialTarget === null,
          })
          set({ votingList: undefined, isSaving: false })
          return voting
        } catch (error) {
          set({ isSaving: false })
          throw error
        }
      },

      deleteVoting: async (id) => {
        set({ isDeleting: true })
        try {
          await window.domain.DeleteVotingVoteUseCase.execute({ id })
          set({ votingList: undefined, isDeleting: false })
        } catch (error) {
          set({ isDeleting: false })
          throw error
        }
      },

      createTokenOnBlockchain: async (votingId) => {
        set({ isPublishing: true })
        try {
          const { safeProposal } = await window.domain.CreateTokenOnBlockchainVoteUseCase.execute({ votingId })
          set({ votingList: undefined, isPublishing: false })
          useSafeTransactionsStore.getState().reset()
          return safeProposal
        } catch (error) {
          set({ isPublishing: false })
          throw error
        }
      },

      setVoteWindow: async (input) => {
        set({ isSaving: true })
        try {
          const { safeProposal } = await window.domain.SetVoteWindowVoteUseCase.execute(input)
          set({ votingList: undefined, isSaving: false })
          useSafeTransactionsStore.getState().reset()
          return safeProposal
        } catch (error) {
          set({ isSaving: false })
          throw error
        }
      },

      toggleAutoUpdatableVoting: async (input) => {
        set({ isTogglingAutoUpdate: true })
        try {
          const { voting } = await window.domain.ToggleAutoUpdatableVotingVoteUseCase.execute(input)
          set({ votingList: get().votingList?.replaceItem(voting), isTogglingAutoUpdate: false })
          return voting
        } catch (error) {
          set({ isTogglingAutoUpdate: false })
          throw error
        }
      },

      refreshAutoUpdatableAllowlists: async () => {
        const { refreshResult } = await window.domain.RefreshAutoUpdatableAllowlistsVoteUseCase.execute()
        set({ votingList: undefined })
        return refreshResult
      },

      reset: () => {
        set({
          ...initialFilters,
          votingList: undefined,
          featuredVotings: [],
          totalCount: 0,
          isLoading: false,
          error: undefined,
          isSaving: false,
          isPublishing: false,
          isDeleting: false,
          isTogglingAutoUpdate: false,
        })
      },
    }),
    { name: "VotingListStore" },
  ),
)
